import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

export function useChat() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [loading, setLoading] = useState(true);

  // Carregar conversas do usuario
  const loadConversations = useCallback(async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from('agency_conversations')
        .select('*')
        .order('updated_at', { ascending: false });

      if (error) throw error;
      setConversations(data || []);
    } catch (err) {
      console.error('Erro ao carregar conversas:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Carregar mensagens de uma conversa
  const loadMessages = useCallback(async (conversationId) => {
    if (!conversationId) return;
    try {
      const { data, error } = await supabase
        .from('agency_messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setMessages(data || []);
    } catch (err) {
      console.error('Erro ao carregar mensagens:', err);
    }
  }, []);

  // Selecionar conversa ativa
  const setActiveConversation = useCallback((id) => {
    setActiveConversationId(id);
    if (id) {
      loadMessages(id);
    } else {
      setMessages([]);
    }
  }, [loadMessages]);

  // Criar nova conversa
  const createConversation = useCallback(async (agentId, clientId = null) => {
    if (!user) return null;
    try {
      const { data, error } = await supabase
        .from('agency_conversations')
        .insert({
          user_id: user.id,
          agent_id: agentId,
          client_id: clientId,
          title: 'Nova conversa',
        })
        .select()
        .single();

      if (error) throw error;
      setConversations(prev => [data, ...prev]);
      setActiveConversationId(data.id);
      setMessages([]);
      return data;
    } catch (err) {
      console.error('Erro ao criar conversa:', err);
      return null;
    }
  }, [user]);

  // Deletar conversa
  const deleteConversation = useCallback(async (id) => {
    try {
      const { error } = await supabase
        .from('agency_conversations')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setConversations(prev => prev.filter(c => c.id !== id));
      if (activeConversationId === id) {
        setActiveConversationId(null);
        setMessages([]);
      }
    } catch (err) {
      console.error('Erro ao deletar conversa:', err);
    }
  }, [activeConversationId]);

  // Adicionar mensagem localmente (otimista)
  const addMessageLocally = useCallback((msg) => {
    setMessages(prev => [...prev, msg]);
  }, []);

  // Atualizar titulo da conversa
  const updateConversationTitle = useCallback(async (id, title) => {
    try {
      await supabase
        .from('agency_conversations')
        .update({ title })
        .eq('id', id);

      setConversations(prev =>
        prev.map(c => c.id === id ? { ...c, title } : c)
      );
    } catch (err) {
      console.error('Erro ao atualizar titulo:', err);
    }
  }, []);

  return {
    conversations,
    messages,
    activeConversationId,
    loading,
    setActiveConversation,
    createConversation,
    deleteConversation,
    addMessageLocally,
    updateConversationTitle,
    loadConversations,
  };
}
